import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SCORECARD_IMPORT_FEATURE } from "../lib/features";
import { useApiData } from "../lib/hooks";
import { buildApiParams } from "../lib/filterRules";
import {
  safeLocalStorageGet,
  safeLocalStorageSet,
  safeLocalStorageRemove,
} from "../lib/storage";
import { ActiveRoundsList } from "../components/ActiveRoundsList";
import { ImportQueueStrip } from "../components/ImportQueueStrip";
import { FormatTiles } from "../components/FormatTiles";
import { RoundSummaryCard } from "../components/RoundSummaryCard";
import { ErrorMessage } from "../components/ErrorMessage";
import { EmptyState } from "../components/EmptyState";
import { useArrowLog } from "../lib/arrowLog";
import { buildTrendSeries } from "../lib/trendSeries";

// Lazy — the shared Score Trend panel statically imports ScoreTrendChart,
// which pulls the whole recharts library; keep it out of the entry bundle so
// first paint doesn't wait on the chart vendor code (#1939 preserves the
// pre-SSOT lazy-chunk split).
const ScoreTrendPanelBody = lazy(() =>
  import("../components/ScoreTrendPanelBody").then((m) => ({ default: m.ScoreTrendPanelBody }))
);

function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

// ── Pin Filter Button ──────────────────────────────────────────────────────

function PinFilterButton({ savedFilters, pinnedId, onPin }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const active = pinnedId != null;
  return (
    <div className="anchor-rel" ref={ref}>
      <button
        className={`icon-btn${active ? " active" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-label="Pin filter"
        title={active ? "Filter pinned — click to change" : "Pin a saved filter"}
      >
        <svg viewBox="0 0 24 24" fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
        </svg>
      </button>
      {open && (
        <div className="pin-filter-popover panel">
          <button
            className={`fp-saved-name${pinnedId == null ? " active" : ""}`}
            onClick={() => { onPin(null); setOpen(false); }}
          >
            No filter
          </button>
          {savedFilters.length === 0 ? (
            <p className="fp-saved-empty">No saved filters yet. Create one in History or Stats.</p>
          ) : (
            savedFilters.map((f) => (
              <button
                key={f.id}
                className={`fp-saved-name${String(pinnedId) === String(f.id) ? " active" : ""}`}
                onClick={() => { onPin(String(f.id)); setOpen(false); }}
              >
                {f.name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Score Trend panel ──────────────────────────────────────────────────────

// Home's Score Trend panel (#1939) — visually identical to Stats' panel: both
// render the shared ScoreTrendPanelBody (head + trend chip + height-160
// chart), with series shaped by the shared lib/trendSeries buildTrendSeries.
// Home passes dim "none" — one "All" line of avg arrow score (avg per arrow is
// scale-agnostic, so mixed 10-zone/5-zone histories share the line) — and
// omits the Stats-only controls (dimension select, per-distance disclosure).
// Home-only chrome: the pin-filter button, the "Stats →" link, and the
// lifetime-totals footer.
function ScoreTrendPanel({ stats, error, savedFilters, pinnedId, onPin, tz, trend }) {
  const trendSeries = buildTrendSeries(stats?.score_trend ?? [], "none");

  const headChrome = (
    <div className="panel-head-chrome">
      <PinFilterButton savedFilters={savedFilters} pinnedId={pinnedId} onPin={onPin} />
      <Link to="/stats" className="panel-link">Stats →</Link>
    </div>
  );

  // Lifetime totals straight from the stats endpoint — uncapped and excluding
  // stats-excluded rounds, unlike the old client-side reduce over a 100-round
  // list (#1859). The endpoint reports no lifetime end count, so the footer
  // shows sessions and arrows.
  const totalSessions = stats?.rounds_shot ?? 0;
  const totalArrows   = stats?.total_arrows ?? 0;
  const footer = (
    <div className="pw-foot">
      <span>
        {totalSessions} session{totalSessions !== 1 ? "s" : ""} · {totalArrows} arrows
      </span>
    </div>
  );

  // Error / empty states render Home's own chart-less shell (Stats doesn't
  // render its panel at all in these cases, so there is no shared visual to
  // reuse) — head chrome and footer stay put either way.
  if (error || trendSeries.length === 0) {
    return (
      <div className="panel">
        <div className="panel-head">
          <div className="panel-title">Score Trend</div>
          {headChrome}
        </div>
        <ErrorMessage message={error} />
        {!error && <EmptyState variant="inline" message="No completed rounds yet." />}
        {footer}
      </div>
    );
  }

  return (
    // Fixed-height fallback (matching the shared chart's 160) so the panel
    // doesn't shift while the lazy chart chunk loads.
    <Suspense
      fallback={
        <div className="panel">
          <div className="panel-head">
            <div className="panel-title">Score Trend</div>
            {headChrome}
          </div>
          <div className="trend-chart-fallback" aria-hidden="true" />
          {footer}
        </div>
      }
    >
      <ScoreTrendPanelBody
        series={trendSeries}
        trend={trend}
        tz={tz}
        headExtras={headChrome}
        footer={footer}
      />
    </Suspense>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

export function Home() {
  const navigate = useNavigate();
  const { activeArcher, hasFeature } = useAuth();
  // Scorecard photo import is a per-account feature (`/api/auth/me` features,
  // missing = off). Off hides the Upload door AND its `U` shortcut — the server
  // refuses the submit too, so this is presentation, not the enforcement.
  const canUpload = hasFeature(SCORECARD_IMPORT_FEATURE);
  const [activeCount, setActiveCount] = useState(0);
  const [recentPin, setRecentPin]     = useState(
    () => safeLocalStorageGet("home_recent_pin") || null
  );
  const [trendPin, setTrendPin]       = useState(
    () => safeLocalStorageGet("home_trend_pin") || null
  );
  // "Log arrows" quick-record sheet (M9.5 #1810) — the single app-level sheet
  // from ArrowLogProvider (#1943); Home no longer mounts its own instance.
  const { openBlank: openLogSheet, isOpen: logSheetOpen } = useArrowLog();
  // Trend-confidence chip signal (#1853, shared with Stats via the SSOT panel
  // #1939) — welch_confidence payload from GET /api/archers/{id}/trends.
  // Unfiltered by the pinned trend filter, same as on Stats.
  const [trend, setTrend] = useState(null);
  const now = useClock();

  useEffect(() => {
    if (activeArcher?.id == null) return;
    api.getTrends(activeArcher.id).then(setTrend).catch(() => {});
  }, [activeArcher?.id]);

  const handleActiveCountChange = useCallback((n) => setActiveCount(n), []);

  // Load saved filters once on mount
  const { data: savedFilters } = useApiData(
    () => api.getSavedFilters(),
    []
  );

  // Compute pinned filter objects (compare as strings to avoid number/string mismatch)
  const recentPinFilter = (savedFilters ?? []).find((f) => String(f.id) === String(recentPin)) ?? null;
  const trendPinFilter  = (savedFilters ?? []).find((f) => String(f.id) === String(trendPin))  ?? null;

  // Fetch recent results (re-runs when pinned filter changes). The list
  // response is self-sufficient for the cards (#1310) — it carries
  // shot_positions + ring counts — so we render the summaries directly with no
  // per-card RoundDetail fetch.
  const {
    data: recentDetails,
    error: recentError,
  } = useApiData(
    async () => {
      const params = recentPinFilter
        ? buildApiParams(new URLSearchParams(recentPinFilter.params_str))
        : {};
      // include=thumbnails: the RoundSummaryCard draws the shot heatmap from
      // shot_positions, which the list no longer ships by default (#2055).
      const summaries = await api.rounds({
        active: false, limit: 100, ...params, include: "thumbnails",
      });
      return summaries.slice(0, 5);
    },
    [recentPinFilter]
  );

  // Fetch archer stats for the Score Trend panel (re-runs when the pinned
  // filter changes). The backend's score_trend already carries per-round
  // arrow_count / max_per_arrow / total, is windowed and ordered server-side,
  // and omits rounds excluded from stats; rounds_shot / total_arrows feed the
  // footer uncapped (#1859).
  const {
    data: archerStats,
    error: trendError,
  } = useApiData(
    () => {
      const params = trendPinFilter
        ? buildApiParams(new URLSearchParams(trendPinFilter.params_str))
        : {};
      return api.getArcherStats(activeArcher.id, params);
    },
    [trendPinFilter, activeArcher?.id],
    { enabled: activeArcher?.id != null }
  );

  // Keyboard shortcuts (#2319): S = score another format, U = upload a
  // scorecard, L = log arrows. Browse/Stats dropped their own shortcuts (B/T)
  // with the quick-action cards they used to sit on — History → and Stats →
  // (Recent Results / Score Trend panels, unchanged below) still reach both.
  useEffect(() => {
    function onKey(e) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // While the Log-arrows sheet is up it owns the keyboard (Escape closes it);
      // navigating away underneath an open sheet would silently drop the draft.
      if (logSheetOpen) return;
      if (e.key === "s" || e.key === "S") navigate("/new-round");
      if (canUpload && (e.key === "u" || e.key === "U")) navigate("/upload");
      if (e.key === "l" || e.key === "L") openLogSheet();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navigate, logSheetOpen, openLogSheet, canUpload]);

  // localStorage persistence helpers — writes are best-effort (#2028); the pin
  // still applies for this session when the store is blocked.
  function setRecentPinAndSave(id) {
    if (id == null) safeLocalStorageRemove("home_recent_pin");
    else safeLocalStorageSet("home_recent_pin", id);
    setRecentPin(id);
  }
  function setTrendPinAndSave(id) {
    if (id == null) safeLocalStorageRemove("home_trend_pin");
    else safeLocalStorageSet("home_trend_pin", id);
    setTrendPin(id);
  }

  const dateStr = now.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).toUpperCase();
  const timeStr = now.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const firstName = activeArcher?.name?.trim().split(/\s+/)[0] || "Archer";
  const hour = now.getHours();
  const partOfDay =
    hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <main className="main">
      <div className="main-head">
        <div>
          <h1>{partOfDay}, {firstName}</h1>
          <div className="greeting">
            {activeCount > 0 ? (
              <>{activeCount === 1 ? "Round" : `${activeCount} rounds`} in progress</>
            ) : activeArcher ? (
              <>Signed in as <b>{activeArcher.name}</b> · @{activeArcher.handle}</>
            ) : (
              "Ready when you are."
            )}
          </div>
        </div>
        <div className="date mono">{dateStr} · {timeStr}</div>
      </div>

      {/* FORMAT TILES — pinned + recent, one-tap start (#2319) */}
      <FormatTiles />

      {/* OR — the doors: Score another format / Upload a scorecard (only with
          the `ocr_scorecard_import` feature) / Log arrows. Replaces the old 4-up .quick grid's Score/Browse/Stats
          slots; History → and Stats → (panels below) still cover those. */}
      <div className="qa-doors">
        <button className="qa" onClick={() => navigate("/new-round")}>
          <div className="qa-icon gold">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <circle cx="12" cy="12" r="5.5" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="12" cy="12" r="0.4" fill="currentColor" />
            </svg>
          </div>
          <div>
            <div className="qa-title">Score another format</div>
            <div className="qa-desc">Pick from the library</div>
          </div>
          <span className="qa-kbd">S</span>
        </button>
        {canUpload && (
        <button className="qa" onClick={() => navigate("/upload")}>
          <div className="qa-icon cinder">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
              <circle cx="12" cy="13" r="3.5" />
            </svg>
          </div>
          <div>
            <div className="qa-title">Upload a scorecard</div>
            <div className="qa-desc">Photo of a signed card</div>
          </div>
          <span className="qa-kbd">U</span>
        </button>
        )}
        <button className="qa" onClick={() => openLogSheet()}>
          {/* Neutral tint on purpose — keeps the tri-color coding of the other two doors */}
          <div className="qa-icon neutral">
            {/* Tally marks — counting arrows, not scoring them */}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 5v14M11 5v14M16 5v14" />
              <path d="M3.5 16.5 19 7" />
            </svg>
          </div>
          <div>
            <div className="qa-title">Log arrows</div>
            <div className="qa-desc">Unscored volume — warm-up, blind bale</div>
          </div>
          <span className="qa-kbd">L</span>
        </button>
      </div>

      {/* SCORECARD IMPORTS — background OCR queue (#1169) */}
      <ImportQueueStrip />

      {/* ACTIVE ROUND */}
      <ActiveRoundsList onCountChange={handleActiveCountChange} />

      {/* RECENT RESULTS + SCORE TREND */}
      <div className="row-2">
        <div className="panel">
          <div className="panel-head">
            <div className="panel-title">Recent results</div>
            <div className="panel-head-chrome">
              <PinFilterButton
                savedFilters={savedFilters ?? []}
                pinnedId={recentPin}
                onPin={setRecentPinAndSave}
              />
              <Link to="/history" className="panel-link">History →</Link>
            </div>
          </div>
          <ErrorMessage message={recentError} />
          <div className="rsc-list">
            {!recentError && (recentDetails ?? []).length === 0 && (
              <EmptyState variant="inline" message="No completed rounds yet — start scoring to fill this in." />
            )}
            {(recentDetails ?? []).map((r) => (
              <RoundSummaryCard key={r.id} round={r} tz={activeArcher?.timezone} />
            ))}
          </div>
        </div>

        <ScoreTrendPanel
          stats={archerStats}
          error={trendError}
          savedFilters={savedFilters ?? []}
          pinnedId={trendPin}
          onPin={setTrendPinAndSave}
          tz={activeArcher?.timezone}
          trend={trend}
        />
      </div>
    </main>
  );
}
